import React, { useState, useMemo, useRef } from 'react';
import { Carousel, Spinner, Nav } from 'react-bootstrap-v5';
import clsx from 'clsx';
import { Offers } from './Offers';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import './MisPlanes.css'; // Estilos de animación

type Props = {
  offers: any[];
  loading: boolean;
};

const MisPlanes: React.FC<Props> = React.memo(({ offers, loading }) => {
  const [activeTab, setActiveTab] = useState('todo');
  const nodeRef = useRef<HTMLDivElement>(null);

  const filteredOffers = useMemo(() => {
    if (!offers) return [];
    if (activeTab === 'semanal') return offers.filter((o) => o.days === 7);
    if (activeTab === 'mensual') return offers.filter((o) => o.days === 30);
    if (activeTab === 'anual') return offers.filter((o) => o.days === 365);
    return offers;
  }, [offers, activeTab]);

  const slides = useMemo(() => {
    const itemsPerSlide = 3;
    const result: any[][] = [];
    for (let i = 0; i < filteredOffers.length; i += itemsPerSlide) {
      result.push(filteredOffers.slice(i, i + itemsPerSlide));
    }
    return result;
  }, [filteredOffers]);

  return (
    <div className="row g-6 g-xl-9 mt-5 mb-5">
      <h2 className="text-center fw-bold mb-4">MIS PLANES</h2>

      {loading ? (
        <div className="d-flex align-items-center justify-content-center min-h-150px">
          <Spinner animation="border" role="status" className="me-2" />
          <span className="text-muted fs-6">Cargando...</span>
        </div>
      ) : offers.length === 0 ? (
        <div className="text-center text-muted min-h-150px py-5">
          No hay ofertas disponibles.
        </div>
      ) : (
        <>
          {/* Tabs */}
          <Nav
            variant="pills"
            className="justify-content-center mb-4"
            activeKey={activeTab}
            onSelect={(tab) => setActiveTab(tab || 'todo')}
          >
            {['todo', 'semanal', 'mensual', 'anual'].map((tab) => (
              <Nav.Item key={tab}>
                <Nav.Link
                  eventKey={tab}
                  className={clsx(
                    'btn btn-sm fw-bold px-4 mx-1',
                    activeTab === tab ? 'btn-primary' : 'btn-light'
                  )}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          {/* Animación visible entre tabs */}
          <SwitchTransition mode="out-in">
            <CSSTransition
              key={activeTab}
              nodeRef={nodeRef}
              timeout={400}
              classNames="slide"
              unmountOnExit
            >
              <div ref={nodeRef}>
                <Carousel
                  controls
                  indicators
                  interval={4000}
                  className="carousel-dark"
                >
                  {slides.map((slide, index) => (
                    <Carousel.Item key={index}>
                      <div className="row row-cols-2 row-cols-lg-5 g-3 justify-content-center">
                        {slide.map((item: any, idx: number) => (
                          <div
                            key={item.id}
                            className="col offer-animate"
                            style={{ animationDelay: `${idx * 100}ms` }}
                          >
                            <Offers data={item} />
                          </div>
                        ))}
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>
            </CSSTransition>
          </SwitchTransition>
        </>
      )}
    </div>
  );
});

export default MisPlanes;
